const SRS_RTC_BASE_URL = 'https://stream.novusomnifleet.com:1986/rtc/v1';

export interface IEmqxCallConfig {
  srs_server_url: string;
  viewer_stream_name: string;
  publisher_stream_name: string;
  stun_uri?: string;
  turn_ip?: string;
  turn_port?: string | number;
  turn_username?: string;
  turn_password?: string;
}

export const resolveEmqxConfig = (data: any): IEmqxCallConfig | null => {
  const config = data?.srs_server_url ? data : data?.data?.srs_server_url ? data.data : data?.data?.data;
  if (config?.srs_server_url && config?.viewer_stream_name && config?.publisher_stream_name) {
    return config;
  }
  return null;
};

export const isAwsCallConfig = (data: any): boolean =>
  Boolean(data?.accessKeyId && data?.secretKeyId && data?.sessionToken && data?.channelArn);

/**
 * Two-way call over SRS (EMQX flow). Exposes the same surface as WebRTCCallService
 * (`peerConnection`, `localStream`, `audioActive`, `toggleAudioMute`, `getRemoteStream`, `endCall`)
 * so callers can swap between the two transports.
 */
export class EmqxCallService {
  peerConnection: RTCPeerConnection | null = null;
  playConnection: RTCPeerConnection | null = null;
  localStream: MediaStream = new MediaStream();
  remoteStream: MediaStream | null = null;
  audioActive = true;

  async startCall(
    audioRef: React.MutableRefObject<HTMLAudioElement | null>,
    config: IEmqxCallConfig,
    handleAudioCallStatus: (connected: boolean) => void,
  ) {
    const { srs_server_url, viewer_stream_name, publisher_stream_name } = config;

    let serverIp = 'localhost';
    try {
      serverIp = srs_server_url.replace('http://', '').replace('https://', '').split(':')[0];
    } catch (e) {
      console.error('[EMQX] Failed to parse SRS IP', e);
    }

    const iceServers: RTCIceServer[] = [];
    if (config.stun_uri) iceServers.push({ urls: config.stun_uri });
    if (config.turn_ip) {
      iceServers.push({
        urls: `turn:${config.turn_ip}:${config.turn_port || 3478}?transport=udp`,
        username: config.turn_username,
        credential: config.turn_password,
      });
    }
    const rtcConfig: RTCConfiguration = { iceServers };

    this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });

    const pcPub = new RTCPeerConnection(rtcConfig);
    this.peerConnection = pcPub;
    this.localStream.getTracks().forEach((track) => pcPub.addTrack(track, this.localStream));

    const offerPub = await pcPub.createOffer();
    await pcPub.setLocalDescription(offerPub);
    const resPub = await fetch(`${SRS_RTC_BASE_URL}/publish/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sdp: offerPub.sdp, streamurl: `webrtc://${serverIp}/live/${viewer_stream_name}` }),
    });
    const answerPub = await resPub.json();
    if (answerPub.code !== 0) throw new Error('SRS Publish Failed');
    await pcPub.setRemoteDescription({ type: 'answer', sdp: answerPub.sdp });

    const pcPlay = new RTCPeerConnection(rtcConfig);
    this.playConnection = pcPlay;
    pcPlay.ontrack = (e) => {
      if (e.track.kind !== 'audio' || !e.streams[0]) return;
      this.remoteStream = e.streams[0];
      if (audioRef.current) {
        audioRef.current.srcObject = e.streams[0];
        audioRef.current.play().catch((err) => console.error('[EMQX] Auto-play failed', err));
      }
    };
    pcPlay.addTransceiver('audio', { direction: 'recvonly' });

    const offerPlay = await pcPlay.createOffer();
    await pcPlay.setLocalDescription(offerPlay);
    const resPlay = await fetch(`${SRS_RTC_BASE_URL}/play/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sdp: offerPlay.sdp, streamurl: `webrtc://${serverIp}/live/${publisher_stream_name}` }),
    });
    const answerPlay = await resPlay.json();
    if (answerPlay.code !== 0) throw new Error('SRS Play Failed');
    await pcPlay.setRemoteDescription({ type: 'answer', sdp: answerPlay.sdp });

    handleAudioCallStatus(true);
  }

  endCall(handleAudioConnection?: (connected: boolean) => void, _handlePublishEvent?: (...args: any[]) => void) {
    this.localStream.getTracks().forEach((track) => track.stop());
    this.localStream = new MediaStream();

    this.peerConnection?.getSenders().forEach((sender) => sender.track?.stop());
    this.peerConnection?.close();
    this.peerConnection = null;

    this.playConnection?.close();
    this.playConnection = null;

    this.remoteStream?.getTracks().forEach((track) => track.stop());
    this.remoteStream = null;

    handleAudioConnection?.(false);
  }

  toggleAudioMute(handleMuted: (trackEnabled: boolean) => void) {
    this.peerConnection?.getSenders().forEach((sender) => {
      if (sender.track?.kind === 'audio') {
        const trackEnabled = !this.audioActive;
        sender.track.enabled = trackEnabled;
        this.audioActive = trackEnabled;
        handleMuted(trackEnabled);
      }
    });
  }

  getRemoteStream(): MediaStream | null {
    return this.remoteStream;
  }

  getLocalStream(): MediaStream {
    return this.localStream;
  }
}
